// src/App.js - MAIN APPLICATION
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ClaimForm from './components/ClaimForm';
import ClaimDetail from './components/ClaimDetail';
import ChangePassword from './components/ChangePassword';
import './App.css';

// Configure axios defaults - use relative URLs since React and Django are served from same domain
axios.defaults.baseURL = '';
axios.defaults.headers.common['Content-Type'] = 'application/json';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always start with no user - require fresh login each time
    localStorage.removeItem('claimsPortalUser');
    localStorage.removeItem('claimsPortalToken');
    delete axios.defaults.headers.common['Authorization'];
    setLoading(false);
  }, []);

  const handleLogin = async (memberNumber, password) => {
    try {
      const response = await axios.post('/api/members/login/', {
        member_number: memberNumber,
        password: password
      });

      if (response.data.success) {
        const userData = response.data.member;
        const token = response.data.token;
        setUser(userData);
        localStorage.setItem('claimsPortalUser', JSON.stringify(userData));
        localStorage.setItem('claimsPortalToken', token);
        // Every request from here on must prove it's this member —
        // the backend rejects member-scoped requests with no valid token.
        axios.defaults.headers.common['Authorization'] = `Token ${token}`;
        return { success: true, data: userData };
      } else {
        return { success: false, error: response.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors ||
                          'Login failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('claimsPortalUser');
    localStorage.removeItem('claimsPortalToken');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Claims Portal...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={
              user ? <Navigate to="/dashboard" replace /> : 
              <Login onLogin={handleLogin} />
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              user ? <Dashboard user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" replace />
            } 
          />
          <Route
            path="/claim/new/:type"
            element={
              user ? <ClaimForm user={user} /> :
              <Navigate to="/login" replace />
            }
          />
          <Route
            path="/claim/:claimId/edit"
            element={
              user ? <ClaimForm user={user} /> :
              <Navigate to="/login" replace />
            }
          />
          <Route
            path="/claim/:claimId"
            element={
              user ? <ClaimDetail user={user} /> :
              <Navigate to="/login" replace />
            }
          />
          <Route
            path="/change-password"
            element={
              user ? <ChangePassword user={user} /> :
              <Navigate to="/login" replace />
            }
          />
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;